// ==============================
// Account management API — staff login accounts (AquaticsUser: COACH + ADMIN).
// Admin-only endpoints under /auth/accounts.
// ==============================

import { apiGet, apiPost, apiPut, apiPatch } from './apiClient';

export type AccountRole = 'COACH' | 'ADMIN';

export interface AquaticsAccount {
  id: number;
  email: string;
  name: string | null;
  role: AccountRole;
  createdAt: string;
  updateAt: string;
  deletionMark: boolean;
}

interface AccountListPayload {
  accounts: AquaticsAccount[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const listAccounts = async (params: {
  page: number;
  limit: number;
  search?: string;
  role?: AccountRole | '';
}) => {
  const query = new URLSearchParams();
  query.set('page', String(params.page));
  query.set('limit', String(params.limit));
  if (params.search) query.set('search', params.search);
  if (params.role) query.set('role', params.role);

  const res = await apiGet<{ data: AccountListPayload }>(
    `/auth/accounts?${query.toString()}`,
  );
  return res.data;
};

export const createAccount = async (payload: {
  name: string;
  email: string;
  password: string;
  role: AccountRole;
}) => {
  const res = await apiPost<{ data: AquaticsAccount }, typeof payload>(
    '/auth/accounts',
    payload,
  );
  return res.data;
};

export const updateAccount = async (
  id: number,
  payload: { name?: string; email?: string; role?: AccountRole },
) => {
  const res = await apiPut<{ data: AquaticsAccount }, typeof payload>(
    `/auth/accounts/${id}`,
    payload,
  );
  return res.data;
};

export const resetAccountPassword = async (id: number, password: string) =>
  apiPatch<{ message: string }, { password: string }>(
    `/auth/accounts/${id}/password`,
    { password },
  );

export const deactivateAccount = async (id: number) =>
  apiPatch<{ message: string }, Record<string, never>>(
    `/auth/accounts/${id}/deactivate`,
    {},
  );

export const reactivateAccount = async (id: number) =>
  apiPatch<{ message: string }, Record<string, never>>(
    `/auth/accounts/${id}/reactivate`,
    {},
  );
